import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import { FaPlus } from "react-icons/fa";
import { AiOutlineEdit } from "react-icons/ai";
import { MdOutlineCancel } from "react-icons/md";
import { IoIosSearch } from "react-icons/io";
import axios from "axios";
import { UserContext } from "../../Context/userContext";
import {
  GETALLUSERS_API,
  DELETEUSERSBYID_API,
} from "../../Constants/apiRoutes";
import "../../style.css";
import {
  StyledTableCell,
  StyledTableRow,
  TablePaginationActions,
} from "../../Components/CustomTablePagination";
import LoadingAnimation from "../../Components/Loading/LoadingAnimation";
import { useConfirmation } from "../../Components/ConfirmDialog";
import { toast, ToastContainer } from "react-toastify";
import { PERMISSIONS } from "../../Constants/permissions";
import api from "../../Components/ProtectedRoute/axiosInstance"; 
const functionsList = [
  { id: 1, name: "Technical Function" },
  { id: 2, name: "Finance" },
  { id: 3, name: "Human Resources" },
  { id: 4, name: "Marketing" },
  { id: 5, name: "Sales" },
  { id: 6, name: "Projects" },
  { id: 7, name: "Legal" },
  { id: 8, name: "Management" },
  { id: 9, name: "Others" },
];

function User() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchName, setSearchName] = useState("");
  const navigate = useNavigate();
  const { setUserDetails } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const { showConfirmation, ConfirmationDialog } = useConfirmation();

  const permissionsRaw = localStorage.getItem("PermissionIDs");
  const permissionsID = permissionsRaw
    ? permissionsRaw.split(",").map(Number)
    : [];
  const hasDeletePermission = permissionsID.includes(PERMISSIONS.DELETE_USER);
  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, searchName]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await api.get(GETALLUSERS_API, {
        params: {
          pageNumber: page + 1,
          pageSize: rowsPerPage,
          SearchText: searchName,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setUsers(response.data.users);
      setTotalUsers(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (userId) => {
    navigate(`/userform/${userId}`);
  };

  const handleDeleteClick = (userId) => {
    if (!hasDeletePermission) {
      toast.error("You do not have permission to delete this user.");
      return;
    }

    showConfirmation(
      "Delete User",
      "Are you sure you want to delete this user? This action cannot be undone.",
      async () => {
        setIsLoading(true);
        try {
          await deleteUserById(userId);
          fetchUsers();
        } catch (error) {
          console.error("Error deleting user:", error);
          throw error; // This will trigger the error toast
        } finally {
          setIsLoading(false);
        }
      }
    );
  };
  const deleteUserById = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      await axios.delete(`${DELETEUSERSBYID_API}/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("User deleted successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  };

  const handleAddUserClick = () => {
    setUserDetails(null);
    navigate("/userform/new");
  };

  const searchItems = (searchValue) => {
    setSearchName(searchValue);
  };

  return (
    <div className="main-container">
      {isLoading && <LoadingAnimation />}
      <ToastContainer />
      <ConfirmationDialog />
      <div className="mx-auto p-6">
        <div className="body-container">
          <h2 className="heading text-brown-700">Users</h2>

          <div className="flex justify-end">
            <ul>
              <li>
                <button
                  type="button"
                  className="action-button flex items-center space-x-2 px-4 py-2"
                  onClick={handleAddUserClick}
                >
                  <FaPlus aria-hidden="true" className="icon" />
                  <span>Add User</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="flex justify-center items-center w-full mt-4 mb-2">
          <div className="relative flex items-center">
            <label htmlFor="searchName" className="sr-only">
              Search
            </label>
            <input
              id="searchName"
              type="text"
              placeholder="Search by Name"
              value={searchName}
              onChange={(e) => searchItems(e.target.value)}
              className="p-2 pr-10 border border-gray-400 rounded-md w-full sm:w-[400px] md:w-[500px] text-sm leading-6 h-[40px]"
            />
            <div className="absolute right-3 text-gray-500">
              <IoIosSearch />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <TableContainer component={Paper} className="sm:p-2 p-1">
            <Table className="min-w-full">
              <TableHead>
                <TableRow>
                  <StyledTableCell className="text-xs sm:text-sm">
                    Name
                  </StyledTableCell>
                  <StyledTableCell className="text-xs sm:text-sm">
                    Roles
                  </StyledTableCell>
                  <StyledTableCell className="text-xs sm:text-sm">
                    Function
                  </StyledTableCell>
                  <StyledTableCell className="text-xs sm:text-sm">
                    Country
                  </StyledTableCell>
                  <StyledTableCell className="text-xs sm:text-sm">
                    Region
                  </StyledTableCell>
                  <StyledTableCell className="text-xs sm:text-sm">
                    Actions
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <StyledTableRow
                    key={user.UserID}
                    className="text-xs sm:text-sm"
                  >
                    <StyledTableCell>
                      <div className="flex items-center">
                        <span>{`${user.FirstName} ${user.LastName}`}</span>
                      </div>
                    </StyledTableCell>
                    <StyledTableCell>
                      <span
                        className={`inline-block min-w-[80px] sm:min-w-[100px] text-center py-0.5 px-2 rounded-full text-[10px] sm:text-sm font-medium ${getRoleColor(
                          user.RoleName|| "-"
                        )}`}
                      >
                        {user.RoleName|| "-"}
                      </span>
                    </StyledTableCell>
                    <StyledTableCell>
                      {user.DepartmentIds &&
                        user.DepartmentIds.split(",")
                          .map((id) => {
                            const match = functionsList.find(
                              (func) => func.id === Number(id.trim())
                            );
                            return match?.name || "";
                          })
                          .filter((name) => name)
                          .join(", ")}
                    </StyledTableCell>
                    <StyledTableCell>{user.CountryName}</StyledTableCell>
                    <StyledTableCell>{user.Region}</StyledTableCell>
                    <StyledTableCell colSpan={2}>
                      <div className="flex space-x-1 sm:space-x-2">
                        <button
                          type="button"
                          onClick={() => handleEditClick(user.UserID)}
                          className="button edit-button text-[10px] sm:text-sm px-2 py-1"
                        >
                          <AiOutlineEdit className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteClick(user.UserID)}
                          className="button delete-button text-[10px] sm:text-sm px-2 py-1"
                        >
                          <MdOutlineCancel className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          Delete
                        </button>
                      </div>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 25]}
                    colSpan={6}
                    count={totalUsers}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
}

const getRoleColor = (role) => {
  switch (role?.toLowerCase()) {
    case "admin":
      return "bg-[#FF3B3066] text-[#FF2D55]";
    case "user":
      return "bg-[#34C75966] text-[#34C759]";
    case "department 1":
      return "bg-[#FFF4E5] text-[#FFA113]";
    case "department 2":
      return "bg-[#E8FFE3] text-[#57CA22]";
    case "department 3":
      return "bg-[#F4E5FF] text-[#A113FF]";
    case "-": return "bg-gray-100 text-gray-800";
    default:
      return "bg-[#F4E5FF] text-[#A113FF]";
  }
};

export default User;
